/**
 * Chat Interface Component
 * Slack-inspired chat interface for stakeholder communication
 */

import React, { useState, useEffect } from 'react';
import './ChatInterface.css';
import ChatChannelList from './ChatChannelList';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { getStakeholders, sendStakeholderMessage, getConversationHistory, getPreviousScenarioContext } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { BookOpen } from 'lucide-react';
import ContextInfoModal from '../Scenario/ContextInfoModal';
import { SCENARIO_DATA } from '../../data/scenarios';

const CHANNELS = [
  { id: 'engineering', name: 'Engineering', icon: '⚙️' },
  { id: 'design', name: 'Design', icon: '🎨' },
  { id: 'data', name: 'Data', icon: '📊' },
  { id: 'leadership', name: 'Leadership', icon: '👔' },
  { id: 'customer-success', name: 'Customer Success', icon: '💬' },
];

const ChatInterface = ({ scenarioContext, onMessageSent, onSendToSensei }) => {
  const { user } = useAuth();
  const [activeChannel, setActiveChannel] = useState('engineering');
  const [messages, setMessages] = useState({});
  const [stakeholders, setStakeholders] = useState({});
  const [stakeholdersLoading, setStakeholdersLoading] = useState(false);
  const [loading, setLoading] = useState({});
  const [previousContext, setPreviousContext] = useState({});
  const [contextModalOpen, setContextModalOpen] = useState(false);
  const scenarioId = scenarioContext?.scenarioId;
  
  // Get scenario data for context modal
  const scenarioData = scenarioId ? SCENARIO_DATA[scenarioId] : null;

  // Get user's first name for personalization
  const getUserFirstName = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name.split(' ')[0];
    }
    return null;
  };

  // Initialize messages and load stakeholders when scenario changes
  useEffect(() => {
    const initialMessages = {};
    const initialLoading = {};
    CHANNELS.forEach(channel => {
      initialMessages[channel.id] = [];
      initialLoading[channel.id] = false;
    });
    setMessages(initialMessages);
    setLoading(initialLoading);
    
    if (scenarioId) {
      // Load stakeholders first, then history
      loadStakeholders().then(() => {
        loadConversationHistory();
      });
      loadPreviousContext();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- scenarioId only; loaders are stable
  }, [scenarioId]);

  // Load conversation history when channel changes (only if stakeholders are loaded)
  useEffect(() => {
    if (scenarioId && stakeholders[activeChannel] !== undefined) {
      loadConversationHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannel, scenarioId]);

  const loadStakeholders = async () => {
    if (!scenarioId) return;
    
    setStakeholdersLoading(true);
    console.log(`[Stakeholders] Loading stakeholders for scenario ${scenarioId}`);
    try {
      // Single API call for all channels (server returns array with channel on each item)
      const allStakeholders = await getStakeholders(scenarioId);
      const stakeholdersMap = {};
      CHANNELS.forEach(channel => {
        stakeholdersMap[channel.id] = (allStakeholders || []).filter(
          (s) => s.channel === channel.id
        );
      });
      setStakeholders(stakeholdersMap);
      console.log(`[Stakeholders] All stakeholders loaded:`, stakeholdersMap);
      return stakeholdersMap;
    } catch (error) {
      console.error(`[Stakeholders] Error loading stakeholders:`, error);
      const emptyMap = {};
      CHANNELS.forEach((c) => (emptyMap[c.id] = []));
      setStakeholders(emptyMap);
      return emptyMap;
    } finally {
      setStakeholdersLoading(false);
    }
  };

  const loadConversationHistory = async () => {
    if (!scenarioId) return;
    
    try {
      console.log(`[Load] Loading conversation history for scenario ${scenarioId}, channel ${activeChannel}`);
      const response = await getConversationHistory(scenarioId, activeChannel);
      const history = response?.messages || [];
      
      if (history && history.length > 0) {
        console.log(`[Load] Found ${history.length} messages in history`);
        // Get current stakeholders for this channel to resolve names
        const channelStakeholders = stakeholders[activeChannel] || [];
        console.log(`[Load] Using ${channelStakeholders.length} stakeholders to resolve names`);
        
        const formattedMessages = history.map((msg, index) => {
          const stakeholderInfo = msg.stakeholderId 
            ? channelStakeholders.find(s => s.id === msg.stakeholderId)
            : null;
          
          // Use saved stakeholder info if available, otherwise look it up
          const stakeholderName = msg.stakeholderName || stakeholderInfo?.name || 'Stakeholder';
          const stakeholderRole = msg.stakeholderRole || stakeholderInfo?.role || '';
          
          return {
            id: `msg-${msg.timestamp || Date.now()}-${index}`,
            type: msg.type === 'stakeholder' ? 'stakeholder' : 'user',
            text: msg.text,
            timestamp: msg.timestamp,
            channel: activeChannel,
            stakeholder: msg.stakeholderId ? {
              id: msg.stakeholderId,
              name: stakeholderName,
              role: stakeholderRole
            } : undefined
          };
        });
        
        setMessages(prev => ({
          ...prev,
          [activeChannel]: formattedMessages
        }));
        
        console.log(`[Load] Loaded ${formattedMessages.length} messages into UI`);
      } else {
        console.log(`[Load] No conversation history found for this channel`);
        // Ensure empty array is set
        setMessages(prev => ({
          ...prev,
          [activeChannel]: []
        }));
      }
    } catch (error) {
      console.error('[Load] Error loading conversation history:', error);
      console.error('[Load] Error details:', {
        scenarioId,
        channel: activeChannel,
        error: error.message,
        response: error.response?.data
      });
      // Set empty array on error to prevent undefined state
      setMessages(prev => ({
        ...prev,
        [activeChannel]: []
      }));
    }
  };

  const loadPreviousContext = async () => {
    if (!scenarioId) return;
    
    try {
      const { context } = await getPreviousScenarioContext(scenarioId, activeChannel);
      setPreviousContext(prev => ({
        ...prev,
        [activeChannel]: context || []
      }));
    } catch (error) {
      console.error('Error loading previous scenario context:', error);
      setPreviousContext(prev => ({
        ...prev,
        [activeChannel]: []
      }));
    }
  };

  // Scroll to bottom is now handled inside ChatMessages component

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      id: `msg-${Date.now()}`,
      type: 'user',
      text: text,
      timestamp: new Date().toISOString(),
      channel: activeChannel
    };

    // Add user message immediately
    setMessages(prev => ({
      ...prev,
      [activeChannel]: [...(prev[activeChannel] || []), userMessage]
    }));

    if (onMessageSent) {
      onMessageSent(userMessage);
    }

    // Get stakeholders for this channel
    const channelStakeholders = stakeholders[activeChannel] || [];
    if (channelStakeholders.length === 0) {
      // No stakeholders in this channel, just show user message
      return;
    }

    // Intelligently select which stakeholder(s) should respond
    // For now, if multiple stakeholders exist, pick the first one
    // In the future, this could use AI to determine relevance
    setLoading(prev => ({ ...prev, [activeChannel]: true }));
    const respondingStakeholder = channelStakeholders[0];

    if (!respondingStakeholder) {
      setLoading(prev => ({ ...prev, [activeChannel]: false }));
      return;
    }

    try {
      console.log('[Chat] Sending message with:', {
        stakeholderId: respondingStakeholder.id,
        scenarioId,
        channel: activeChannel,
        messageLength: text.length,
        hasScenarioContext: !!scenarioContext
      });

      const response = await sendStakeholderMessage(
        respondingStakeholder.id,
        text,
        {
          scenarioContext: {
            ...scenarioContext,
            currentTaskTitle: scenarioContext?.currentTaskTitle,
            currentTaskDescription: scenarioContext?.currentTaskDescription,
            currentTaskProductSenseChallenge: scenarioContext?.tasks?.[scenarioContext?.currentTask]?.productSenseChallenge,
            // Add more context
            description: scenarioContext?.description,
            timeline: scenarioContext?.timeline,
            constraints: scenarioContext?.constraints,
          },
          companyContext: {
            ...scenarioContext?.company || {},
            // Ensure all company data is passed
            metrics: scenarioContext?.company?.metrics,
            competitors: scenarioContext?.company?.competitors,
            recentEvents: scenarioContext?.company?.recentEvents,
          },
          conversationHistory: (messages[activeChannel] || []).slice(-5).map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.text
          })),
          userFirstName: getUserFirstName()
        },
        scenarioId,
        activeChannel
      );

      const stakeholderMessage = {
        id: `msg-${Date.now()}-response`,
        type: 'stakeholder',
        text: response.response,
        timestamp: response.timestamp,
        channel: activeChannel,
        stakeholder: response.stakeholder || {
          id: respondingStakeholder.id,
          name: respondingStakeholder.name,
          role: respondingStakeholder.role
        }
      };

      setMessages(prev => ({
        ...prev,
        [activeChannel]: [...(prev[activeChannel] || []), stakeholderMessage]
      }));
      
      // Note: Messages are saved on the backend after response is generated
      // The backend saves both user message and stakeholder response together
      console.log('[Chat] Message and response added to UI. Backend should have saved to database.');
    } catch (error) {
      console.error('[Chat] Error sending message:', error);
      console.error('[Chat] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      // Provide user-friendly error messages
      let errorText = 'Failed to get response. ';
      if (error.response?.status === 401 || error.response?.status === 403) {
        errorText += 'Authentication error. Please refresh the page.';
      } else if (error.response?.status === 500) {
        const serverError = error.response?.data?.error || error.message;
        errorText += serverError || 'Server error. Please check your OpenAI API key configuration.';
        if (serverError?.includes('OPENAI_API_KEY')) {
          errorText += ' Make sure OPENAI_API_KEY is set in the server .env file.';
        }
      } else if (error.message?.includes('API key') || error.message?.includes('OPENAI_API_KEY')) {
        errorText += 'OpenAI API key is missing or invalid. Please check server configuration.';
      } else if (error.message?.includes('rate limit') || error.response?.status === 429) {
        errorText += 'Rate limit exceeded. Please try again in a moment.';
      } else if (error.response?.status === 404) {
        errorText += 'Stakeholder not found. Please refresh the page.';
      } else {
        errorText += error.response?.data?.error || error.message || 'Please check your API configuration.';
      }
      
      const errorMessage = {
        id: `msg-${Date.now()}-error`,
        type: 'system',
        text: errorText,
        timestamp: new Date().toISOString(),
        channel: activeChannel
      };
      setMessages(prev => ({
        ...prev,
        [activeChannel]: [...(prev[activeChannel] || []), errorMessage]
      }));
    } finally {
      setLoading(prev => ({ ...prev, [activeChannel]: false }));
    }
  };

  return (
    <div className="chat-interface">
      <ChatChannelList
        channels={CHANNELS}
        activeChannel={activeChannel}
        onChannelSelect={setActiveChannel}
        unreadCounts={{}}
        stakeholders={stakeholders}
        stakeholdersLoading={stakeholdersLoading}
      />
      
      <div className="chat-main">
        <div className="chat-header">
          <div className="chat-header-title">
            <h2>#{CHANNELS.find(c => c.id === activeChannel)?.name}</h2>
            {stakeholders[activeChannel] && stakeholders[activeChannel].length > 0 && (
              <div className="chat-stakeholders-inline">
                {stakeholders[activeChannel].map((stakeholder, index) => (
                  <span key={stakeholder.id} className="stakeholder-badge-inline">
                    {index > 0 && <span className="stakeholder-separator">•</span>}
                    {stakeholder.name}
                  </span>
                ))}
              </div>
            )}
            <button 
              className="context-info-button"
              onClick={() => setContextModalOpen(true)}
              title="What You Know"
              aria-label="Show context information"
            >
              <BookOpen size={18} />
            </button>
          </div>
          <div className="chat-header-info">
            {stakeholders[activeChannel] && stakeholders[activeChannel].length > 0 && (
              <div className="chat-stakeholders">
                {stakeholders[activeChannel].map(stakeholder => (
                  <span key={stakeholder.id} className="stakeholder-badge">
                    {stakeholder.name} ({stakeholder.role})
                  </span>
                ))}
              </div>
            )}
            {previousContext[activeChannel] && previousContext[activeChannel].length > 0 && (
              <div className="context-indicator">
                <span className="context-badge">
                  Continuing from {previousContext[activeChannel].length} previous scenario{previousContext[activeChannel].length > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>

        <ChatMessages
          messages={messages[activeChannel] || []}
          loading={loading[activeChannel] || false}
          onSendToSensei={onSendToSensei}
          scenarioContext={scenarioContext}
        />

        <ChatInput
          onSend={handleSendMessage}
          disabled={loading[activeChannel] || false}
          placeholder={`Message #${CHANNELS.find(c => c.id === activeChannel)?.name.toLowerCase()}`}
        />
      </div>
      
      <ContextInfoModal
        isOpen={contextModalOpen}
        onClose={() => setContextModalOpen(false)}
        scenario={scenarioData}
        scenarioContext={scenarioContext}
      />
    </div>
  );
};

export default ChatInterface;



